import React from 'react';
import { useSelector } from 'react-redux';
import t from 'react-translate';
import { isEmpty } from 'underscore';

import { RootState } from 'redux/schemas';
import { CourseAliases } from 'redux/schemas/models/course';
import { ComponentType, NLectureComponent, QuestionSetType } from 'redux/schemas/models/lecture-component';
import { NProgressiveQuiz } from 'redux/schemas/models/progressive-quiz';
import { NAllQuestionViewQuiz } from 'redux/schemas/models/all-question-view-quiz';
import { getCourseAliases } from 'redux/selectors/course';
import { getPointsConfiguration } from 'redux/selectors/points-configurations';

import ActivityPoints from 'shared/components/activity/activity-points';
import NvIcon from 'shared/components/nv-icon';
import { LecturePageMode } from 'lecture_pages/components';
import { css } from '@emotion/core';
import { gray2 } from 'styles/global_defaults/colors';
import { getComponentActivity } from 'redux/selectors/quizzes';

type PointsComponentProps = {
  type: QuestionSetType;
  mode: LecturePageMode;
  lectureComponent: NLectureComponent<QuestionSetType>;
};

const styles = css`
  .dot-separator {
    border-color: ${gray2};
  }
`;

const PointsComponent = (props: PointsComponentProps) => {
  const { type, mode, lectureComponent } = props;

  const activity = useSelector((state) => getComponentActivity(state, lectureComponent));
  const { hardDeadlinePassed } = activity;
  const isViewMode = mode === LecturePageMode.VIEW;
  const isCompleted = activity.progress === 'completed';
  const isInProgress = activity.progress === 'in_progress';
  const isMissed = activity.progress === 'missed' || hardDeadlinePassed;
  const notStarted = activity.progress === 'not_started';

  const isAllQuestionViewQuiz = type === ComponentType.QUIZ;

  const getProgressiveQuizCorrectAnswersCount = () => {
    /**
     * As per the back-end request, the correct_answers_count field should be used
     * to get the correctAnswersCount when the quiz is in the resume state.
     * In other cases, the score field should be used.
     */
    if ((activity as NProgressiveQuiz).resumeQuiz) {
      return (activity as NProgressiveQuiz).correctAnswersCount;
    }
    return (activity as NProgressiveQuiz)?.submission?.score;
  };

  const correctAnswersCount = isAllQuestionViewQuiz
    ? ((activity as NAllQuestionViewQuiz)?.submission?.score ?? 0)
    : getProgressiveQuizCorrectAnswersCount();

  const getPointConfigurationId = () => activity?.pointsConfiguration as number;

  const pointsConfiguration = useSelector((state) => (
    getPointsConfiguration(state, getPointConfigurationId() || 0)
  ));
  const aliases = useSelector<RootState, CourseAliases>(
    (state: RootState) => getCourseAliases(state),
  );

  const isRequirementEnabled = pointsConfiguration && !pointsConfiguration?.rewardsPointsProportionally;
  const requiredCorrectQuestionsCount = isRequirementEnabled ? Math.ceil(activity.answerableQuestionsCount * pointsConfiguration?.threshold) : 0;

  const getPointsReceived = () => {
    if (type === ComponentType.SURVEY) {
      return t.LECTURE_PAGES.COMPONENTS.ACTIVITY.POINTS({
        ...aliases.pointsAliases,
        pointsCount: activity.pointsReceived,
      });
    }

    return activity.pointsReceived;
  };


  const pointsText = (() => {
    if (lectureComponent.type === ComponentType.SURVEY) {
      return null;
    }
    const notCompletedText = pointsConfiguration?.rewardsPointsProportionally ? t.POINTS.UP_TO_X_POINTS({
      maxPoints: activity.totalPoints?.[0],
      pointsAliasSingularized: aliases.pointsAliases.pointAlias,
      pointsAliasPluralized: aliases.pointsAliases.pointsAlias,
    }) : undefined;

    if (isViewMode) {
      if (isCompleted) {
        return undefined;
      }

      return notCompletedText;
    }
    return notCompletedText;
  })();

  const label = (() => {
    if (lectureComponent.type === ComponentType.SURVEY) {
      return null;
    }

    const awardForPassingScore = !pointsConfiguration?.rewardsPointsProportionally;

    if (awardForPassingScore) {
      if (isViewMode) {
        if (isCompleted || isMissed) {
          if (correctAnswersCount === activity.answerableQuestionsCount) {
            return t.POINTS.ALL_CORRECT();
          }

          return isMissed
            ? t.POINTS.GET_X_OR_MORE_CORRECT_ANSWERS(requiredCorrectQuestionsCount)
            : t.TIMED_QUIZZES.ANSWERED_X_CORRECT(correctAnswersCount, activity.answerableQuestionsCount);
        }
        if (isAllQuestionViewQuiz) {
          if (!isEmpty(activity?.submission?.responses)) {
            return t.POINTS.NEED_MORE_FOR_POINTS(
              requiredCorrectQuestionsCount.toString(),
              correctAnswersCount.toString(),
            );
          }
          return t.POINTS.GET_X_OR_MORE_CORRECT_ANSWERS(requiredCorrectQuestionsCount);
        }

        if (isInProgress && !(activity as NProgressiveQuiz).resumeQuiz) {
          return t.POINTS.NEED_MORE_FOR_POINTS(
            requiredCorrectQuestionsCount.toString(),
            correctAnswersCount.toString(),
          );
        }

        return t.POINTS.GET_X_OR_MORE_CORRECT_ANSWERS(requiredCorrectQuestionsCount);
      }

      return t.POINTS.GET_X_OR_MORE_CORRECT_ANSWERS(requiredCorrectQuestionsCount);
    }
    if (!awardForPassingScore && isViewMode) {
      if (isCompleted) {
        if (correctAnswersCount === activity.answerableQuestionsCount) {
          return t.POINTS.ALL_CORRECT();
        }
        return t.TIMED_QUIZZES.ANSWERED_X_CORRECT(correctAnswersCount, activity.answerableQuestionsCount);
      }
    }

    return undefined;
  })();

  if (!!activity.answerableQuestionsCount
    && (pointsConfiguration && !!pointsConfiguration?.points)) {
    const questionsText = (() => {
      if (notStarted || isMissed) {
        return t.QUIZZES.QUESTIONS(activity.answerableQuestionsCount.toString());
      }

      if (correctAnswersCount < requiredCorrectQuestionsCount) {
        return t.POINTS.GET_X_OR_MORE_CORRECT_ANSWERS(requiredCorrectQuestionsCount);
      }

      return undefined;
    })();

    if (pointsConfiguration?.rewardsPointsProportionally
        && isCompleted) {
      return (
        <div css={styles} className='activity-points mb-2'>
          <div className='d-flex'>
            {activity.pointsReceived ? (
              <div className='d-flex text-small font-weight-bold'>
                <NvIcon icon='highlight' size='xss-smallest' className='text-success mr-2' />
                <span>
                  <span className='text-success'>{getPointsReceived()}</span>
                  { (activity.totalPoints?.[0] > activity.pointsReceived) && (
                  <span className='text-gray-2'>
                    /
                    {activity.totalPoints?.[0]}
                  </span>
                  )}
                </span>
              </div>
            ) : (
              <div className='d-flex text-small font-weight-bold'>
                <NvIcon icon='points' size='xss-smallest' className='text-gray-2 mr-2' />
                <span className='text-gray-2'>
                  {t.POINTS.UP_TO_X_POINTS({
                    maxPoints: activity.totalPoints?.[0],
                    pointsAliasSingularized: aliases.pointsAliases.pointAlias,
                    pointsAliasPluralized: aliases.pointsAliases.pointsAlias,
                  })}
                </span>
              </div>
            )}
            {label && (
            <div>
              <span className='dot-separator' />
              <span className='text-gray-2'>
                {label}
              </span>
            </div>
            )}
          </div>
        </div>
      );
    }
    return (
      <div className='activity-points mb-2'>
        <ActivityPoints
          label={label}
          pointsText={pointsText}
          preLabelText={questionsText}
          totalPoints={activity.totalPoints?.[0]}
          pointsReceived={activity.pointsReceived}
        />
      </div>
    );
  }

  const questionsText = (() => {
    if (notStarted || isMissed) {
      return t.QUIZZES.QUESTIONS(activity.answerableQuestionsCount.toString());
    }

    if (correctAnswersCount < activity.answerableQuestionsCount) {
      return t.POINTS.GOT_POINTS('', activity.answerableQuestionsCount.toString(), correctAnswersCount.toString());
    }

    if (correctAnswersCount === activity.answerableQuestionsCount && (lectureComponent.type !== ComponentType.SURVEY)) {
      return t.POINTS.ALL_CORRECT();
    }

    return undefined;
  })();

  if (!questionsText) {
    return null;
  }

  return (
    <div className='activity-points'>
      <span className='text-gray-2'>
        {questionsText}
      </span>
    </div>
  );
};

export default PointsComponent;
